import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useImpersonation } from '@/hooks/useImpersonation';
import { useLanguage } from '@/hooks/useLanguage';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Package, 
  ShoppingCart, 
  Plus, 
  Edit, 
  Trash2, 
  AlertCircle, 
  DollarSign, 
  LogOut, 
  Eye,
  Users,
  BarChart3,
  Settings,
  Store,
  TrendingUp,
  Warehouse,
  ShoppingBag,
  RefreshCw,
  Clock,
  Bell,
  Search,
  X,
  ArrowUpDown
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { formatPriceWithCurrency } from '@/utils/currency';
import { useCurrency } from '@/hooks/useCurrency';
import { OrderFilters as VendorOrderFilters } from '@/components/vendor/OrderFilters';
import { ProductTranslationsForm } from '@/components/vendor/ProductTranslationsForm';
import { ProductImageUpload } from '@/components/products/ProductImageUpload';
import { VendorBalance } from '@/components/vendor/VendorBalance';
import VendorFinancialDashboard from '@/components/vendor/VendorFinancialDashboard';
import NotificationSettings from '@/components/vendor/NotificationSettings';
import { VendorWithdrawalRequest } from '@/components/vendor/VendorWithdrawalRequest';
import { useCategories } from '@/hooks/useCategories';
import { VendorInvoiceGenerator } from '@/components/vendor/VendorInvoiceGenerator';
import { ColorManagement } from '@/components/vendor/ColorManagement';
import { SizeManagement } from '@/components/vendor/SizeManagement';

interface Product {
  id: string;
  title: string;
  description: string | null;
  title_translations?: Record<string, string>;
  description_translations?: Record<string, string>;
  subtitle?: string | null;
  subtitle_translations?: Record<string, string>;
  colors?: Array<{name: string, hex: string, stock: number}>;
  sizes?: Array<{size: string, stock: number}>;
  price_cents: number;
  currency: string;
  stock: number;
  category: string | null;
  brand: string | null;
  images: string[];
  active: boolean;
  created_at: string;
}

interface Relay {
  id: string;
  name: string;
  address: string;
  active: boolean;
}

interface Order {
  id: string;
  order_number: string;
  status: string;
  amount_cents: number;
  currency: string;
  created_at: string;
  product: {
    title: string;
    images: string[];
  };
  relay: {
    name: string;
    address: string;
  };
}

interface Manager {
  id: string;
  manager_id: string;
  role: string;
  manager: {
    full_name: string;
    phone: string | null;
    system_role: string;
  };
}

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  position: string;
  user_id: string | null;
}

interface BusinessHours {
  [key: string]: {
    open: string;
    close: string;
    closed: boolean;
  };
}

interface PaymentMethodChangeRequest {
  id: string;
  vendor_id: string;
  requested_by: string;
  proposed_business_phone: string | null;
  proposed_mobile_provider: string | null;
  proposed_bank_account: string | null;
  proposed_bank_name: string | null;
  proposed_bank_first_name: string | null;
  proposed_bank_last_name: string | null;
  status: 'pending' | 'approved' | 'rejected';
  rejection_reason: string | null;
  approved_by: string | null;
  created_at: string;
}

const VendorAccount = () => {
  const { user, profile, signOut, isEffectiveVendor } = useAuth();
  const { getEffectiveUserId } = useImpersonation();
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { displayCurrency, convertPrice } = useCurrency();
  const { categories } = useCategories(true);
  
  // Charger les vendor IDs (propriétaire + magasins gérés)
  const [vendorIds, setVendorIds] = useState<string[]>([]);
  const [accessLoading, setAccessLoading] = useState(true);
  
  useEffect(() => {
    const fetchVendorAccess = async () => {
      const effectiveUserId = getEffectiveUserId();
      if (!effectiveUserId) {
        setAccessLoading(false);
        return;
      }

      try {
        // Récupérer d'abord les magasins gérés (prioritaires)
        const { data: managedStores } = await supabase
          .from('vendor_managers')
          .select('vendor_id')
          .eq('manager_id', effectiveUserId);

        const ids: string[] = [];
        
        // Les vendeurs gérés en premier (c'est au nom de ces vendeurs qu'on crée les produits)
        if (managedStores && managedStores.length > 0) {
          managedStores.forEach(store => {
            if (!ids.includes(store.vendor_id)) {
              ids.push(store.vendor_id);
            }
          });
        }
        
        // Ajouter l'utilisateur lui-même en dernier (fallback si pas de vendeurs gérés)
        if (!ids.includes(effectiveUserId)) {
          ids.push(effectiveUserId);
        }

        setVendorIds(ids);
      } catch (error) {
        console.error('[VendorAccount] Error fetching vendor access:', error);
        setVendorIds([effectiveUserId]); // Fallback: au moins l'utilisateur impersonifié ou réel
      } finally {
        setAccessLoading(false);
      }
    };

    fetchVendorAccess();
  }, [getEffectiveUserId]);

  // Helper function to get translated text
  const getTranslatedText = (translations: Record<string, string> | undefined, fallback: string): string => {
    if (!translations || Object.keys(translations).length === 0) return fallback;
    
    // Try current language
    if (translations[language]) return translations[language];
    
    // Fallback to French
    if (translations['fr']) return translations['fr'];
    
    // Fallback to any available translation
    const firstValue = Object.values(translations)[0];
    return firstValue || fallback;
  };

  // Helper function to format category labels
  const formatCategoryLabel = (slug: string): string => {
    const categoryLabels: Record<string, string> = {
      'mode-beaute': 'Mode & Beauté',
      'maison-jardin': 'Maison & Jardin',
      'electronique': 'Électronique',
      'sante-bien-etre': 'Santé & Bien-être',
      'livres-culture': 'Livres & Culture',
      'auto-moto': 'Auto & Moto',
      'sport-loisirs': 'Sport & Loisirs',
      'enfants-bebes': 'Enfants & Bébés',
      'jeux-jouets': 'Jeux & Jouets',
      'high-tech': 'High-Tech',
      'alimentation': 'Alimentation',
      'services': 'Services',
    };
    
    return categoryLabels[slug] || slug.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };
  
  const [products, setProducts] = useState<Product[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [managers, setManagers] = useState<Manager[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [relays, setRelays] = useState<Relay[]>([]);
  const [totalRevenueGross, setTotalRevenueGross] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [showManagerDialog, setShowManagerDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<string>('');
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [paymentChangeRequests, setPaymentChangeRequests] = useState<PaymentMethodChangeRequest[]>([]);
  
  // États pour les filtres de commandes
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [orderSearchQuery, setOrderSearchQuery] = useState('');
  const [orderDateFilter, setOrderDateFilter] = useState('all');
  const [orderSortBy, setOrderSortBy] = useState('created_at');
  const [orderSortOrder, setOrderSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // États pour les filtres de produits
  const [productStatusFilter, setProductStatusFilter] = useState('all');
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [productDateFilter, setProductDateFilter] = useState('all');
  const [productSortBy, setProductSortBy] = useState('created_at');
  const [productSortOrder, setProductSortOrder] = useState<'asc' | 'desc'>('desc');
  
  const [productForm, setProductForm] = useState({
    title: '',
    description: '',
    title_translations: {} as Record<string, string>,
    description_translations: {} as Record<string, string>,
    subtitle: '',
    subtitle_translations: {} as Record<string, string>,
    colors: [] as Array<{name: string, hex: string, stock: number}>,
    sizes: [] as Array<{size: string, stock: number}>,
    price_cents: 0,
    currency: 'CDF',
    stock: 0,
    category: '',
    brand: '',
    model: '',
    selectedRelays: [] as string[],
    images: [] as string[]
  });
  
  // Stocker le prix de référence pour éviter les erreurs d'arrondi en chaîne
  const [referencePriceState, setReferencePriceState] = useState({
    price_cents: 0,
    currency: 'CDF'
  });
  
  const defaultBusinessHours: BusinessHours = {
    monday: { open: "09:00", close: "18:00", closed: false },
    tuesday: { open: "09:00", close: "18:00", closed: false },
    wednesday: { open: "09:00", close: "18:00", closed: false },
    thursday: { open: "09:00", close: "18:00", closed: false },
    friday: { open: "09:00", close: "18:00", closed: false },
    saturday: { open: "09:00", close: "16:00", closed: false },
    sunday: { open: "10:00", close: "16:00", closed: false }
  };

  const getBusinessHours = (hours: unknown): BusinessHours => {
    if (!hours || typeof hours !== 'object' || Object.keys(hours as object).length === 0) {
      return defaultBusinessHours;
    }
    return hours as BusinessHours;
  };

  const [shopForm, setShopForm] = useState({
    business_name: profile?.business_name || '',
    business_address: profile?.business_address || '',
    business_email: profile?.business_email || '',
    business_phone: profile?.business_phone || '',
    business_hours: getBusinessHours(profile?.business_hours),
    default_withdrawal_method: (profile as any)?.default_withdrawal_method || '',
    default_mobile_provider: (profile as any)?.default_mobile_provider || '',
    default_mobile_money_number: (profile as any)?.default_mobile_money_number || '',
    default_bank_account: (profile as any)?.default_bank_account || '',
    default_bank_name: (profile as any)?.default_bank_name || '',
    default_bank_first_name: (profile as any)?.default_bank_first_name || '',
    default_bank_last_name: (profile as any)?.default_bank_last_name || '',
    auto_withdrawal_enabled: Boolean((profile as any)?.auto_withdrawal_enabled),
    auto_withdrawal_frequency: (profile as any)?.auto_withdrawal_frequency || 'daily',
    auto_withdrawal_time: (profile as any)?.auto_withdrawal_time || '20:00',
    auto_withdrawal_day_of_week: String((profile as any)?.auto_withdrawal_day_of_week ?? '1'),
    auto_withdrawal_day_of_month: String((profile as any)?.auto_withdrawal_day_of_month ?? '20'),
    auto_withdrawal_window_start_day: String((profile as any)?.auto_withdrawal_window_start_day ?? '20'),
    auto_withdrawal_window_end_day: String((profile as any)?.auto_withdrawal_window_end_day ?? '25'),
    auto_withdrawal_min_balance_cents: String((profile as any)?.auto_withdrawal_min_balance_cents ?? '0')
  });

  useEffect(() => {
    if (user && vendorIds.length > 0 && !accessLoading) {
      fetchData();
      fetchRelays();
    }
  }, [user, vendorIds, accessLoading]);

  const effectiveUserId = getEffectiveUserId();
  const targetVendorId = vendorIds.length > 0 ? vendorIds[0] : effectiveUserId;

  // Initialiser le formulaire du magasin quand le profil est chargé
  useEffect(() => {
    const loadTargetVendorProfile = async () => {
      if (!profile || !targetVendorId) return;

      const sourceProfile = targetVendorId === effectiveUserId
        ? profile
        : await supabase
            .from('profiles')
            .select('*')
            .eq('id', targetVendorId)
            .single()
            .then(({ data }) => data || profile)
            .catch(() => profile);

      setShopForm({
        business_name: sourceProfile.business_name || '',
        business_address: sourceProfile.business_address || '',
        business_email: sourceProfile.business_email || '',
        business_phone: (sourceProfile as any)?.business_phone || '',
        business_hours: getBusinessHours(sourceProfile.business_hours),
        default_withdrawal_method: (sourceProfile as any)?.default_withdrawal_method || '',
        default_mobile_provider: (sourceProfile as any)?.default_mobile_provider || '',
        default_mobile_money_number: (sourceProfile as any)?.default_mobile_money_number || '',
        default_bank_account: (sourceProfile as any)?.default_bank_account || '',
        default_bank_name: (sourceProfile as any)?.default_bank_name || '',
        default_bank_first_name: (sourceProfile as any)?.default_bank_first_name || '',
        default_bank_last_name: (sourceProfile as any)?.default_bank_last_name || '',
        auto_withdrawal_enabled: Boolean((sourceProfile as any)?.auto_withdrawal_enabled),
        auto_withdrawal_frequency: (sourceProfile as any)?.auto_withdrawal_frequency || 'daily',
        auto_withdrawal_time: (sourceProfile as any)?.auto_withdrawal_time || '20:00',
        auto_withdrawal_day_of_week: String((sourceProfile as any)?.auto_withdrawal_day_of_week ?? '1'),
        auto_withdrawal_day_of_month: String((sourceProfile as any)?.auto_withdrawal_day_of_month ?? '20'),
        auto_withdrawal_window_start_day: String((sourceProfile as any)?.auto_withdrawal_window_start_day ?? '20'),
        auto_withdrawal_window_end_day: String((sourceProfile as any)?.auto_withdrawal_window_end_day ?? '25'),
        auto_withdrawal_min_balance_cents: String((sourceProfile as any)?.auto_withdrawal_min_balance_cents ?? '0')
      });
    };

    loadTargetVendorProfile();
  }, [profile, targetVendorId, effectiveUserId]);

  const fetchRelays = async () => {
    try {
      const { data, error } = await supabase
        .from('relays')
        .select('id, name, address, active')
        .eq('active', true)
        .order('name');

      if (error) throw error;
      setRelays(data || []);
    } catch (error) {
      console.error('Erreur lors de la récupération des points relais:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setRefreshing(true);
    try {
      await Promise.all([
        fetchProducts(),
        fetchOrders(),
        fetchTotalRevenue(),
        fetchManagers(),
        fetchEmployees(),
        fetchPaymentChangeRequests()
      ]);
      toast({
        title: t('vendorAccount.dataUpdated'),
        description: t('vendorAccount.dataUpdatedDesc')
      });
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      toast({
        title: t('vendorAccount.loadingError'),
        description: t('vendorAccount.loadingErrorDesc'),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchPaymentChangeRequests = async () => {
    if (vendorIds.length === 0) return;

    try {
      const { data, error } = await supabase
        .from('vendor_payment_method_change_requests' as any)
        .select('*')
        .in('vendor_id', vendorIds)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setPaymentChangeRequests((data || []) as PaymentMethodChangeRequest[]);
    } catch (error) {
      console.error('Erreur lors du chargement des demandes de changement de paiement:', error);
    }
  };

  const handleReviewPaymentChangeRequest = async (requestId: string, approve: boolean) => {
    try {
      const { data, error } = await supabase.rpc('review_vendor_payment_method_change', {
        p_request_id: requestId,
        p_approve: approve,
        p_rejection_reason: approve ? null : 'Rejete par administrateur/gestionnaire'
      });

      if (error) throw error;

      if (data?.success === false) {
        throw new Error(data.error || 'Impossible de traiter la demande');
      }

      if (approve) {
        const effectiveUserId = getEffectiveUserId();
        const { data: updatedProfile, error: profileError } = await supabase
          .from('profiles')
          .select('business_phone, default_mobile_provider, default_mobile_money_number, default_bank_account, default_bank_name, default_bank_first_name, default_bank_last_name')
          .eq('id', effectiveUserId)
          .maybeSingle();

        if (!profileError && updatedProfile) {
          setShopForm(prev => ({
            ...prev,
            business_phone: (updatedProfile as any)?.business_phone || '',
            default_mobile_provider: (updatedProfile as any)?.default_mobile_provider || '',
            default_mobile_money_number: (updatedProfile as any)?.default_mobile_money_number || '',
            default_bank_account: (updatedProfile as any)?.default_bank_account || '',
            default_bank_name: (updatedProfile as any)?.default_bank_name || '',
            default_bank_first_name: (updatedProfile as any)?.default_bank_first_name || '',
            default_bank_last_name: (updatedProfile as any)?.default_bank_last_name || ''
          }));
        }
      }

      toast({
        title: approve ? 'Changement valide' : 'Demande rejetee',
        description: data?.message || (approve ? 'Les informations sensibles ont ete appliquees' : 'La demande a ete rejetee')
      });

      await fetchData();
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const fetchProducts = async () => {
    if (vendorIds.length === 0) return;
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .in('vendor_id', vendorIds)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProducts((data || []) as any);
    } catch (error) {
      console.error('Erreur lors de la récupération des produits:', error);
    }
  };

  const fetchOrders = async () => {
    if (vendorIds.length === 0) return;
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          product:products(title, images),
          relay:relays(name, address),
          order_items(
            product_id,
            quantity,
            unit_price,
            products(title, images)
          )
        `)
        .in('vendor_id', vendorIds)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      
      // Enrich orders with product from order_items if not directly available
      const enrichedOrders = (data || []).map(order => {
        let productData = order.product;
        if (!productData && order.order_items && order.order_items.length > 0) {
          productData = (order.order_items[0] as any).products;
        }
        return { ...order, product: productData };
      });
      
      setOrders(enrichedOrders);
    } catch (error) {
      console.error('Erreur lors de la récupération des commandes:', error);
    }
  };

  const fetchTotalRevenue = async () => {
    if (vendorIds.length === 0) return;
    try {
      // Récupérer toutes les ventes (type credit)
      const { data, error } = await supabase
        .from('vendor_transactions')
        .select('gross_amount_cents, currency, type')
        .in('vendor_id', vendorIds)
        .eq('type', 'credit');

      if (error) throw error;
      
      // Convertir chaque transaction dans la devise d'affichage
      const totalInDisplayCurrency = (data || []).reduce((sum, t) => {
        // convertPrice attend des centimes et retourne des centimes
        const convertedAmountCents = convertPrice(t.gross_amount_cents, t.currency, displayCurrency);
        return sum + (convertedAmountCents / 100);
      }, 0);
      
      // Arrondir vers le haut et limiter à 2 décimales
      const roundedTotal = Math.ceil(totalInDisplayCurrency * 100) / 100;
      setTotalRevenueGross(parseFloat(roundedTotal.toFixed(2)));
    } catch (error) {
      console.error('Erreur lors du calcul des revenus:', error);
      setTotalRevenueGross(0);
    }
  };

  const fetchManagers = async () => {
    if (vendorIds.length === 0) return;
    try {
      const { data, error } = await supabase
        .from('vendor_managers')
        .select(`
          *,
          manager:profiles!manager_id(full_name, phone, system_role)
        `)
        .in('vendor_id', vendorIds);

      if (error) throw error;
      setManagers((data || []) as any);
    } catch (error) {
      console.error('Erreur lors de la récupération des gestionnaires:', error);
    }
  };

  const fetchEmployees = async () => {
    // Seuls les magasins internes et l'administrateur peuvent voir les employés
    if (!profile?.is_internal && profile?.role !== 'admin') {
      return;
    }

    try {
      const { data, error } = await supabase
        .from('employees')
        .select('id, first_name, last_name, email, phone, position, user_id')
        .eq('status', 'active')
        .order('first_name');

      if (error) throw error;
      setEmployees(data || []);
    } catch (error) {
      console.error('Erreur lors de la récupération des employés:', error);
    }
  };

  const handleProductSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const effectiveUserId = getEffectiveUserId();
    if (productForm.selectedRelays.length === 0) {
      toast({
        title: t('vendorAccount.error'),
        description: "Veuillez sélectionner au moins un point relais",
        variant: "destructive"
      });
      return;
    }

    try {
      if (editingProduct) {
        const { error } = await supabase
          .from('products')
          .update({
            title: productForm.title,
            description: productForm.description,
            title_translations: productForm.title_translations,
            description_translations: productForm.description_translations,
            subtitle: productForm.subtitle || null,
            subtitle_translations: productForm.subtitle_translations,
            colors: productForm.colors,
            sizes: productForm.sizes,
            price_cents: productForm.price_cents,
            currency: productForm.currency,
            stock: productForm.stock,
            category: productForm.category,
            brand: productForm.brand || null,
            model: productForm.model || null,
            images: productForm.images
          })
          .eq('id', editingProduct.id);

        if (error) throw error;

        // Update relay availability
        // First delete existing relations
        await supabase
          .from('product_relay_availability')
          .delete()
          .eq('product_id', editingProduct.id);

        // Then insert new ones
        const relayInserts = productForm.selectedRelays.map(relayId => ({
          product_id: editingProduct.id,
          relay_id: relayId
        }));

        const { error: relayError } = await supabase
          .from('product_relay_availability')
          .insert(relayInserts);

        if (relayError) throw relayError;

        toast({ title: t('vendorAccount.productUpdated') });
      } else {
        // Utiliser le premier vendor_id (qui est le vendeur géré si gestionnaire, ou l'utilisateur lui-même)
        const primaryVendorId = vendorIds.length > 0 ? vendorIds[0] : effectiveUserId;
        
        console.log('[VendorAccount] Creating product for vendor:', primaryVendorId, 'Current user:', effectiveUserId);
        
        const { data: newProduct, error } = await supabase
          .from('products')
          .insert({
            vendor_id: primaryVendorId,
            title: productForm.title,
            description: productForm.description,
            title_translations: productForm.title_translations,
            description_translations: productForm.description_translations,
            subtitle: productForm.subtitle || null,
            subtitle_translations: productForm.subtitle_translations,
            colors: productForm.colors,
            sizes: productForm.sizes,
            price_cents: productForm.price_cents,
            currency: productForm.currency,
            stock: productForm.stock,
            category: productForm.category,
            brand: productForm.brand || null,
            model: productForm.model || null,
            images: productForm.images
          })
          .select()
          .single();

        if (error) throw error;

        // Insert relay availability for new product
        const relayInserts = productForm.selectedRelays.map(relayId => ({
          product_id: newProduct.id,
          relay_id: relayId
        }));

        const { error: relayError } = await supabase
          .from('product_relay_availability')
          .insert(relayInserts);

        if (relayError) throw relayError;

        toast({ title: t('vendorAccount.productCreated') });
      }

      fetchProducts();
      setShowProductDialog(false);
      resetProductForm();
    } catch (error: any) {
      toast({ 
        title: t('vendorAccount.error'), 
        description: error.message,
        variant: "destructive" 
      });
    }
  };

  const resetProductForm = () => {
    setProductForm({
      title: '',
      description: '',
      title_translations: {},
      description_translations: {},
      subtitle: '',
      subtitle_translations: {},
      colors: [],
      sizes: [],
      price_cents: 0,
      currency: 'CDF',
      stock: 0,
      category: '',
      brand: '',
      model: '',
      selectedRelays: [],
      images: []
    });
    // Réinitialiser le prix de référence
    setReferencePriceState({
      price_cents: 0,
      currency: 'CDF'
    });
    setEditingProduct(null);
  };

  const handleEditProduct = async (product: Product) => {
    // Fetch relay availability for this product
    const { data: relayData, error } = await supabase
      .from('product_relay_availability')
      .select('relay_id')
      .eq('product_id', product.id);

    if (error) {
      console.error('Error fetching relay availability:', error);
    }

    setProductForm({
      title: product.title,
      description: product.description || '',
      title_translations: product.title_translations || {},
      description_translations: product.description_translations || {},
      subtitle: product.subtitle || '',
      subtitle_translations: product.subtitle_translations || {},
      colors: product.colors || [],
      sizes: product.sizes || [],
      price_cents: product.price_cents,
      currency: product.currency || 'CDF',
      stock: product.stock,
      category: product.category || '',
      brand: product.brand || '',
      model: (product as any).model || '',
      selectedRelays: relayData?.map(r => r.relay_id) || [],
      images: product.images
    });
    // Initialiser le prix de référence avec le prix du produit
    setReferencePriceState({
      price_cents: product.price_cents,
      currency: product.currency
    });
    setEditingProduct(product);
    setShowProductDialog(true);
  };

  const handleDeleteProduct = async (productId: string) => {
    if (!confirm(t('vendorAccount.confirmDelete'))) return;

    try {
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', productId);

      if (error) throw error;
      toast({ title: t('vendorAccount.productDeleted') });
      fetchProducts();
    } catch (error: any) {
      toast({ 
        title: "Erreur", 
        description: error.message,
        variant: "destructive" 
      });
    }
  };

  const handleAddManager = async () => {
    const effectiveUserId = getEffectiveUserId();
    if (!selectedEmployee) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner un employé",
        variant: "destructive"
      });
      return;
    }

    try {
      const employee = employees.find(e => e.user_id === selectedEmployee);
      if (!employee?.user_id) {
        toast({
          title: "Erreur",
          description: "Cet employé n'a pas de compte utilisateur associé",
          variant: "destructive"
        });
        return;
      }

      const { error } = await supabase
        .from('vendor_managers')
        .insert({
          vendor_id: effectiveUserId,
          manager_id: employee.user_id,
          role: 'manager',
          created_by: effectiveUserId
        });

      if (error) throw error;

      toast({
        title: "Gestionnaire ajouté",
        description: `${employee.first_name} ${employee.last_name} a été ajouté à votre équipe`
      });

      setShowManagerDialog(false);
      setSelectedEmployee('');
      fetchManagers();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleRemoveManager = async (managerId: string, managerName: string) => {
    if (!confirm(`Êtes-vous sûr de vouloir retirer ${managerName} de votre équipe ?`)) return;

    try {
      const { error } = await supabase
        .from('vendor_managers')
        .delete()
        .eq('id', managerId);

      if (error) throw error;

      toast({
        title: "Gestionnaire retiré",
        description: `${managerName} a été retiré de votre équipe`
      });

      fetchManagers();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleShopSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const effectiveUserId = getEffectiveUserId();
    if (!effectiveUserId) return;

    try {
      const { data: currentSensitive, error: currentSensitiveError } = await supabase
        .from('profiles')
        .select('business_phone, default_mobile_provider, default_mobile_money_number, default_bank_account, default_bank_name, default_bank_first_name, default_bank_last_name')
        .eq('id', targetVendorId)
        .maybeSingle();

      if (currentSensitiveError) throw currentSensitiveError;

      const normalize = (value: string | null | undefined) => (value || '').trim();

      const hasSensitiveChange =
        normalize(shopForm.business_phone) !== normalize((currentSensitive as any)?.business_phone) ||
        normalize(shopForm.default_mobile_provider) !== normalize((currentSensitive as any)?.default_mobile_provider) ||
        normalize(shopForm.default_mobile_money_number) !== normalize((currentSensitive as any)?.default_mobile_money_number) ||
        normalize(shopForm.default_bank_account) !== normalize((currentSensitive as any)?.default_bank_account) ||
        normalize(shopForm.default_bank_name) !== normalize((currentSensitive as any)?.default_bank_name) ||
        normalize(shopForm.default_bank_first_name) !== normalize((currentSensitive as any)?.default_bank_first_name) ||
        normalize(shopForm.default_bank_last_name) !== normalize((currentSensitive as any)?.default_bank_last_name);

      const { error } = await supabase
        .from('profiles')
        .update({
          business_name: shopForm.business_name,
          business_address: shopForm.business_address,
          business_email: shopForm.business_email,
          business_hours: shopForm.business_hours,
          default_withdrawal_method: shopForm.default_withdrawal_method || null,
          auto_withdrawal_enabled: shopForm.auto_withdrawal_enabled,
          auto_withdrawal_frequency: shopForm.auto_withdrawal_frequency || 'daily',
          auto_withdrawal_time: shopForm.auto_withdrawal_time || '20:00',
          auto_withdrawal_day_of_week: shopForm.auto_withdrawal_frequency === 'weekly' ? Number(shopForm.auto_withdrawal_day_of_week) : null,
          auto_withdrawal_day_of_month: shopForm.auto_withdrawal_frequency === 'monthly' ? Number(shopForm.auto_withdrawal_day_of_month) : null,
          auto_withdrawal_window_start_day: shopForm.auto_withdrawal_frequency === 'monthly' ? Number(shopForm.auto_withdrawal_window_start_day) : null,
          auto_withdrawal_window_end_day: shopForm.auto_withdrawal_frequency === 'monthly' ? Number(shopForm.auto_withdrawal_window_end_day) : null,
          auto_withdrawal_min_balance_cents: Number(shopForm.auto_withdrawal_min_balance_cents || 0)
        })
        .eq('id', targetVendorId);

      if (error) throw error;

      if (hasSensitiveChange) {
        const { data: requestData, error: requestError } = await supabase.rpc('request_vendor_payment_method_change', {
          p_vendor_id: targetVendorId,
          p_business_phone: shopForm.business_phone,
          p_mobile_provider: shopForm.default_mobile_provider,
          p_mobile_money_number: shopForm.default_mobile_money_number,
          p_bank_account: shopForm.default_bank_account,
          p_bank_name: shopForm.default_bank_name,
          p_bank_first_name: shopForm.default_bank_first_name,
          p_bank_last_name: shopForm.default_bank_last_name
        });

        if (requestError) throw requestError;
        if (requestData?.success === false) {
          throw new Error(requestData.error || 'La demande de changement n\'a pas pu etre envoyee');
        }
      }
      
      toast({
        title: hasSensitiveChange ? 'Demande envoyee' : 'Informations du magasin mises a jour',
        description: hasSensitiveChange
          ? 'Les coordonnees sensibles sont en attente de validation par administrateur/gestionnaire du magasin.'
          : 'Les informations du magasin ont ete sauvegardees.'
      });

      await fetchData();
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const updateBusinessHours = (day: string, field: 'open' | 'close' | 'closed', value: string | boolean) => {
    setShopForm(prev => ({
      ...prev,
      business_hours: {
        ...prev.business_hours,
        [day]: {
          ...prev.business_hours[day],
          [field]: value
        }
      }
    }));
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'PENDING_PAYMENT': { label: t('status.pending_payment'), variant: 'secondary' as const },
      'PAID': { label: t('status.paid'), variant: 'default' as const },
      'AWAITING_DROP_OFF': { label: t('status.awaiting_drop_off'), variant: 'destructive' as const },
      'AVAILABLE_FOR_PICKUP': { label: t('status.available_for_pickup'), variant: 'default' as const },
      'PICKED_UP': { label: t('status.picked_up'), variant: 'secondary' as const },
      'EXPIRED': { label: t('status.expired'), variant: 'destructive' as const },
      'DISPUTE': { label: t('status.dispute'), variant: 'destructive' as const },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || { label: status, variant: 'secondary' as const };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  // Filtrer et trier les commandes
  const filteredAndSortedOrders = orders
    .filter(order => {
      // Filtre par statut
      if (orderStatusFilter !== 'all' && order.status !== orderStatusFilter) return false;

      // Filtre par recherche
      if (orderSearchQuery) {
        const query = orderSearchQuery.toLowerCase();
        return (
          order.order_number?.toLowerCase().includes(query) ||
          order.product?.title?.toLowerCase().includes(query) ||
          order.relay?.name?.toLowerCase().includes(query)
        );
      }

      // Filtre par date
      if (orderDateFilter !== 'all') {
        const orderDate = new Date(order.created_at);
        const now = new Date();
        
        switch (orderDateFilter) {
          case 'today':
            if (orderDate.toDateString() !== now.toDateString()) return false;
            break;
          case 'week':
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            if (orderDate < weekAgo) return false;
            break;
          case 'month':
            if (orderDate.getMonth() !== now.getMonth() || orderDate.getFullYear() !== now.getFullYear()) return false;
            break;
          case 'year':
            if (orderDate.getFullYear() !== now.getFullYear()) return false;
            break;
        }
      }

      return true;
    })
    .sort((a, b) => {
      let comparison = 0;
      
      switch (orderSortBy) {
        case 'amount':
          comparison = a.amount_cents - b.amount_cents;
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
        case 'order_number':
          comparison = (a.order_number || '').localeCompare(b.order_number || '');
          break;
        case 'created_at':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        default:
          comparison = 0;
      }
      
      return orderSortOrder === 'asc' ? comparison : -comparison;
    });

  const clearOrderFilters = () => {
    setOrderStatusFilter('all');
    setOrderSearchQuery('');
    setOrderDateFilter('all');
    setOrderSortBy('created_at');
    setOrderSortOrder('desc');
  };

  const handleOrderSortOrderToggle = () => {
    setOrderSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  // Statistiques calculées
  // Revenus totaux = montant BRUT depuis vendor_transactions
  const totalRevenue = totalRevenueGross;
  
  const pendingOrders = orders.filter(o => o.status === 'AWAITING_DROP_OFF').length;
  const lowStockProducts = products.filter(p => p.stock <= 5).length;
  const totalStock = products.reduce((sum, p) => sum + p.stock, 0);

  // Check if user can access vendor dashboard
  if (!isEffectiveVendor) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container py-16">
          <Card className="max-w-md mx-auto">
            <CardHeader className="text-center">
              <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
              <CardTitle>{t('unauthorizedAccess')}</CardTitle>
              <CardDescription>
                {t('unauthorizedDesc')}
              </CardDescription>
            </CardHeader>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container py-16">
          <div className="flex justify-center">
            <div className="text-center">
              <Package className="h-12 w-12 animate-spin mx-auto mb-4" />
              <p>{t('vendorAccount.loading')}</p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Fonction pour filtrer et trier les produits
  const getFilteredProducts = () => {
    let filtered = [...products];
    
    // Filtre par statut
    if (productStatusFilter !== 'all') {
      filtered = filtered.filter(p => 
        productStatusFilter === 'active' ? p.active : !p.active
      );
    }
    
    // Filtre par recherche (titre, catégorie, marque)
    if (productSearchQuery.trim()) {
      const query = productSearchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        getTranslatedText(p.title_translations, p.title).toLowerCase().includes(query) ||
        p.category?.toLowerCase().includes(query) ||
        p.brand?.toLowerCase().includes(query)
      );
    }
    
    // Filtre par période
    if (productDateFilter !== 'all') {
      const now = new Date();
      filtered = filtered.filter(p => {
        const productDate = new Date(p.created_at);
        const diffDays = Math.floor((now.getTime() - productDate.getTime()) / (1000 * 60 * 60 * 24));
        
        switch (productDateFilter) {
          case 'today': return diffDays === 0;
          case 'week': return diffDays <= 7;
          case 'month': return diffDays <= 30;
          case 'year': return diffDays <= 365;
          default: return true;
        }
      });
    }
    
    // Tri
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (productSortBy) {
        case 'created_at':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case 'title':
          comparison = getTranslatedText(a.title_translations, a.title).localeCompare(
            getTranslatedText(b.title_translations, b.title)
          );
          break;
        case 'price':
          comparison = a.price_cents - b.price_cents;
          break;
        case 'stock':
          comparison = a.stock - b.stock;
          break;
        default:
          comparison = 0;
      }
      
      return productSortOrder === 'asc' ? comparison : -comparison;
    });
    
    return filtered;
  };
  
  const filteredProducts = getFilteredProducts();

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="container py-4 md:py-8 pb-24 md:pb-8">
        <div className="max-w-7xl">
          {/* Header - Mobile Optimized */}
          <div className="mb-4 md:mb-8">
            <div className="flex items-center justify-between mb-2">
              <div className="flex-1 min-w-0">
                <h1 className="text-xl md:text-3xl font-bold truncate">{t('vendor.title')}</h1>
                <p className="text-xs md:text-sm text-muted-foreground truncate">
                  {profile?.business_name || profile?.full_name} • {t('vendor.subtitle')}
                </p>
              </div>
              <Button 
                variant="ghost"
                size="sm"
                onClick={fetchData}
                disabled={refreshing}
                className="flex-shrink-0"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span className="hidden md:inline md:ml-2">
                  {refreshing ? t('vendorAccount.refreshing') : t('vendor.refresh')}
                </span>
              </Button>
            </div>
          </div>

          {/* Dashboard Stats - Mobile Grid 2x2 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-8">
            <Card className="border-gray-200 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-3 md:p-4">
                <CardTitle className="text-xs md:text-sm font-medium text-gray-600">{t('vendorAccount.products')}</CardTitle>
                <Package className="h-4 w-4 text-gray-400" />
              </CardHeader>
              <CardContent className="p-3 md:p-4 pt-0">
                <div className="text-xl md:text-2xl font-bold">{products.length}</div>
                <p className="text-xs text-muted-foreground">
                  {products.filter(p => p.active).length} {t('vendorAccount.activeProducts')}
                </p>
                <p className="text-xs text-green-600 font-medium mt-1 hidden md:block">
                  {t('vendorAccount.stockTotal')} {totalStock} {t('vendorAccount.units')}
                </p>
              </CardContent>
            </Card>

            <Card className="border-gray-200 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-3 md:p-4">
                <CardTitle className="text-xs md:text-sm font-medium text-gray-600">{t('vendorAccount.orders')}</CardTitle>
                <ShoppingCart className="h-4 w-4 text-gray-400" />
              </CardHeader>
              <CardContent className="p-3 md:p-4 pt-0">
                <div className="text-xl md:text-2xl font-bold">{orders.length}</div>
                <p className="text-xs text-muted-foreground">
                  {pendingOrders} {t('vendorAccount.toProcess')}
                </p>
              </CardContent>
            </Card>

            <Card className="border-gray-200 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-3 md:p-4">
                <CardTitle className="text-xs md:text-sm font-medium text-gray-600">{t('vendorAccount.revenue')}</CardTitle>
                <DollarSign className="h-4 w-4 text-gray-400" />
              </CardHeader>
              <CardContent className="p-3 md:p-4 pt-0">
                <div className="text-xl md:text-2xl font-bold">
                  {formatPriceWithCurrency(Math.round(totalRevenue * 100), displayCurrency, displayCurrency, convertPrice)}
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('vendorAccount.totalSales')}
                </p>
              </CardContent>
            </Card>

            <Card className="border-gray-200 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-3 md:p-4">
                <CardTitle className="text-xs md:text-sm font-medium text-gray-600">{t('vendorAccount.lowStock')}</CardTitle>
                <AlertCircle className="h-4 w-4 text-gray-400" />
              </CardHeader>
              <CardContent className="p-3 md:p-4 pt-0">
                <div className="text-xl md:text-2xl font-bold text-orange-600">{lowStockProducts}</div>
                <p className="text-xs text-muted-foreground">
                  {t('vendorAccount.productsLowStock')}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Alertes */}
          {(lowStockProducts > 0 || pendingOrders > 0) && (
            <div className="mb-8 space-y-4">
              {lowStockProducts > 0 && (
                <Card className="border-orange-200 bg-orange-50">
                  <CardContent className="pt-3 md:pt-6 px-3 md:px-6">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 md:h-5 md:w-5 text-orange-600 flex-shrink-0" />
                      <span className="font-medium text-orange-800 text-xs md:text-sm">
                        {lowStockProducts} {t('vendorAccount.lowStockAlert')}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {pendingOrders > 0 && (
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center space-x-2">
                      <ShoppingBag className="h-5 w-5 text-red-600" />
                      <span className="font-medium text-red-800">
                        {pendingOrders} {t('vendorAccount.pendingDropAlert')}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Main Content */}
          <Tabs defaultValue="products" className="w-full">
            <TabsList className={`grid w-full gap-1 h-auto p-1 ${(profile?.is_internal || profile?.role === 'admin') ? 'grid-cols-3 md:grid-cols-7' : 'grid-cols-3 md:grid-cols-6'}`}>
              <TabsTrigger value="products" className="flex-col gap-0.5 h-auto py-2 px-1 text-[10px] md:text-sm md:flex-row md:gap-2">
                <Package className="h-3.5 w-3.5 md:h-4 md:w-4" />
                <span>{t('vendorAccount.products')}</span>
              </TabsTrigger>
              <TabsTrigger value="orders" className="flex-col gap-0.5 h-auto py-2 px-1 text-[10px] md:text-sm md:flex-row md:gap-2">
                <ShoppingCart className="h-3.5 w-3.5 md:h-4 md:w-4" />
                <span>{t('vendorAccount.orders')}</span>
              </TabsTrigger>
              <TabsTrigger value="finances" className="flex-col gap-0.5 h-auto py-2 px-1 text-[10px] md:text-sm md:flex-row md:gap-2">
                <DollarSign className="h-3.5 w-3.5 md:h-4 md:w-4" />
                <span>Finances</span>
              </TabsTrigger>
              <TabsTrigger value="stock" className="flex-col gap-0.5 h-auto py-2 px-1 text-[10px] md:text-sm md:flex-row md:gap-2">
                <Warehouse className="h-3.5 w-3.5 md:h-4 md:w-4" />
                <span>{t('vendorAccount.stock')}</span>
              </TabsTrigger>
              {(profile?.is_internal || profile?.role === 'admin') && (
                <TabsTrigger value="managers" className="flex-col gap-0.5 h-auto py-2 px-1 text-[10px] md:text-sm md:flex-row md:gap-2">
                  <Users className="h-3.5 w-3.5 md:h-4 md:w-4" />
                  <span>{t('vendorAccount.team')}</span>
                </TabsTrigger>
              )}
              <TabsTrigger value="settings" className="flex-col gap-0.5 h-auto py-2 px-1 text-[10px] md:text-sm md:flex-row md:gap-2">
                <Settings className="h-3.5 w-3.5 md:h-4 md:w-4" />
                <span>{t('vendorAccount.settings')}</span>
              </TabsTrigger>
              <TabsTrigger value="notifications" className="flex-col gap-0.5 h-auto py-2 px-1 text-[10px] md:text-sm md:flex-row md:gap-2">
                <Bell className="h-3.5 w-3.5 md:h-4 md:w-4" />
                <span>Notifications</span>
              </TabsTrigger>
            </TabsList>

            {/* Products Tab */}
            <TabsContent value="products" className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>{t('vendor.product_catalog')}</CardTitle>
                      <CardDescription>
                        {t('vendor.manage_inventory')}
                      </CardDescription>
                    </div>
                    <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
                      <DialogTrigger asChild>
                        <Button onClick={resetProductForm} className="text-xs md:text-sm h-8 md:h-10 px-2 md:px-4">
                          <Plus className="h-3.5 w-3.5 md:h-4 md:w-4 md:mr-2" />
                          <span className="hidden md:inline ml-2">{t('vendor.new_product')}</span>
                          <span className="md:hidden ml-1">Ajouter</span>
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>
                            {editingProduct ? t('product.edit') : t('product.new')}
                          </DialogTitle>
                          <DialogDescription>
                            {t('product.formDesc')}
                          </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleProductSubmit} className="space-y-4">
                          <ProductTranslationsForm
                            titleTranslations={productForm.title_translations}
                            descriptionTranslations={productForm.description_translations}
                            productId={editingProduct?.id || undefined}
                            onTitleChange={(translations) => setProductForm(prev => ({ 
                              ...prev, 
                              title_translations: translations,
                              title: translations.fr || translations.en || translations['lg-fr'] || translations.sw || ''
                            }))}
                            onDescriptionChange={(translations) => setProductForm(prev => ({ 
                              ...prev, 
                              description_translations: translations,
                              description: translations.fr || translations.en || translations['lg-fr'] || translations.sw || ''
                            }))}
                          />
                          
                          {/* Sous-titre */}
                          <div>
                            <Label htmlFor="subtitle">Sous-titre (optionnel)</Label>
                            <Input
                              id="subtitle"
                              value={productForm.subtitle}
                              onChange={(e) => setProductForm(prev => ({ ...prev, subtitle: e.target.value }))}
                              placeholder="Ex: Compatible avec tous les smartphones"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Information courte supplémentaire affichée sous le titre
                            </p>
                          </div>

                          {/* Gestion des couleurs */}
                          <ColorManagement
                            colors={productForm.colors}
                            onChange={(colors) => setProductForm(prev => ({ ...prev, colors }))}
                          />

                          {/* Gestion des tailles */}
                          <SizeManagement
                            sizes={productForm.sizes}
                            onChange={(sizes) => setProductForm(prev => ({ ...prev, sizes }))}
                          />

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="brand">Marque</Label>
                              <Input
                                id="brand"
                                value={productForm.brand}
                                onChange={(e) => setProductForm(prev => ({ ...prev, brand: e.target.value }))}
                                placeholder="Ex: Samsung, Apple, Nike..."
                              />
                            </div>
                            <div>
                              <Label htmlFor="model">Modèle</Label>
                              <Input
                                id="model"
                                value={productForm.model}
                                onChange={(e) => setProductForm(prev => ({ ...prev, model: e.target.value }))}
                                placeholder="Ex: iPhone 15 Pro, Galaxy S24..."
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="price">{t('product.price')} ({productForm.currency})</Label>
                              <Input
                                id="price"
                                type="number"
                                step="0.01"
                                min="0"
                                value={productForm.price_cents / 100}
                                onChange={(e) => {
                                  const newPriceCents = Math.round(parseFloat(e.target.value) * 100);
                                  setProductForm(prev => ({ ...prev, price_cents: newPriceCents }));
                                  // Mettre à jour le prix de référence quand l'utilisateur change le prix manuellement
                                  setReferencePriceState({
                                    price_cents: newPriceCents,
                                    currency: productForm.currency
                                  });
                                }}
                                required
                              />
                            </div>
                            <div>
                              <Label htmlFor="stock">{t('product.stock')}</Label>
                              <Input
                                id="stock"
                                type="number"
                                min="0"
                                value={productForm.stock}
                                onChange={(e) => setProductForm(prev => ({ ...prev, stock: parseInt(e.target.value) || 0 }))}
                                required
                              />
                            </div>
                          </div>
                          <div>
                            <Label htmlFor="currency">{t('product.currency')}</Label>
                            <Select
                              value={productForm.currency}
                              onValueChange={(value) => {
                                // Taux de conversion précis (mis à jour régulièrement)
                                const rates: Record<string, Record<string, number>> = {
                                  'CDF': { 'USD': 1/2800, 'EUR': 1/3000, 'CDF': 1 },
                                  'USD': { 'CDF': 2800, 'EUR': 0.93, 'USD': 1 },
                                  'EUR': { 'CDF': 3000, 'USD': 1.08, 'EUR': 1 }
                                };
                                
                                // Toujours convertir depuis le prix de référence (pas de conversion en chaîne)
                                const refPrice = referencePriceState.price_cents;
                                const refCurrency = referencePriceState.currency;
                                
                                let newPrice = productForm.price_cents;
                                
                                // Si on revient à la devise de référence, remettre le prix de référence directement
                                if (refCurrency === value) {
                                  newPrice = refPrice;
                                }
                                // Sinon, convertir depuis la devise de référence
                                else if (refPrice > 0 && refCurrency) {
                                  const conversionRate = rates[refCurrency]?.[value] || 1;
                                  newPrice = Math.round(refPrice * conversionRate);
                                }
                                
                                setProductForm(prev => ({ 
                                  ...prev, 
                                  currency: value,
                                  price_cents: newPrice
                                }));
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue>
                                  {productForm.currency === 'CDF' && t('currency.cdf_full')}
                                  {productForm.currency === 'USD' && t('currency.usd_full')}
                                  {productForm.currency === 'EUR' && t('currency.eur_full')}
                                  {!productForm.currency && t('product.selectCurrency')}
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="CDF">{t('currency.cdf_full')}</SelectItem>
                                <SelectItem value="USD">{t('currency.usd_full')}</SelectItem>
                                <SelectItem value="EUR">{t('currency.eur_full')}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor="category">{t('product.category')}</Label>
                            <Select
                              value={productForm.category}
                              onValueChange={(value) => setProductForm(prev => ({ ...prev, category: value }))}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={t('product.selectCategory') || 'Sélectionner une catégorie'}>
                                  {productForm.category && (
                                    categories.find(c => c.slug === productForm.category)?.name || productForm.category
                                  )}
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                {categories.map((category) => (
                                  <SelectItem key={category.id} value={category.slug}>
                                    {category.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <ProductImageUpload
                            images={productForm.images}
                            onImagesChange={(images) => setProductForm(prev => ({ ...prev, images }))}
                            maxImages={5}
                          />
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <Label>Points relais disponibles</Label>
                              {relays.length > 0 && (
                                <div className="flex gap-2">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setProductForm(prev => ({
                                      ...prev,
                                      selectedRelays: relays.map(r => r.id)
                                    }))}
                                  >
                                    Tout sélectionner
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setProductForm(prev => ({
                                      ...prev,
                                      selectedRelays: []
                                    }))}
                                  >
                                    Tout désélectionner
                                  </Button>
                                </div>
                              )}
                            </div>
                            <div className="space-y-2 max-h-40 overflow-y-auto border rounded-md p-3 mt-2">
                              {relays.length === 0 ? (
                                <p className="text-sm text-muted-foreground">Aucun point relais disponible</p>
                              ) : (
                                relays.map((relay) => (
                                  <div key={relay.id} className="flex items-start space-x-2">
                                    <input
                                      type="checkbox"
                                      id={`relay-${relay.id}`}
                                      checked={productForm.selectedRelays.includes(relay.id)}
                                      onChange={(e) => {
                                        const relayId = relay.id;
                                        setProductForm(prev => ({
                                          ...prev,
                                          selectedRelays: e.target.checked
                                            ? [...prev.selectedRelays, relayId]
                                            : prev.selectedRelays.filter(id => id !== relayId)
                                        }));
                                      }}
                                      className="mt-1"
                                    />
                                    <label htmlFor={`relay-${relay.id}`} className="text-sm cursor-pointer flex-1">
                                      <div className="font-medium">{relay.name}</div>
                                      <div className="text-muted-foreground text-xs">{relay.address}</div>
                                    </label>
                                  </div>
                                ))
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                              Sélectionnez les points relais où ce produit sera disponible
                            </p>
                          </div>
                          <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setShowProductDialog(false)}>
                              {t('product.cancel')}
                            </Button>
                            <Button type="submit">
                              {editingProduct ? t('product.modify') : t('product.create')}
                            </Button>
                          </DialogFooter>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Barre de filtres et recherche */}
                  <div className="mb-6 space-y-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Search className="h-4 w-4" />
                      <span className="font-medium">{t('vendor.filters.title')}</span>
                      {(productStatusFilter !== 'all' || productSearchQuery || productDateFilter !== 'all' || productSortBy !== 'created_at') && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setProductStatusFilter('all');
                            setProductSearchQuery('');
                            setProductDateFilter('all');
                            setProductSortBy('created_at');
                            setProductSortOrder('desc');
                          }}
                          className="ml-auto h-7 px-2 text-xs"
                        >
                          <X className="h-3 w-3 mr-1" />
                          {t('vendor.filters.reset')}
                        </Button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      {/* Filtre Statut */}
                      <div>
                        <Label className="text-xs text-muted-foreground mb-1.5 block">{t('vendor.filters.status')}</Label>
                        <Select value={productStatusFilter} onValueChange={setProductStatusFilter}>
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder={t('vendor.filters.allStatuses')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">{t('vendor.filters.allStatuses')}</SelectItem>
                            <SelectItem value="active">{t('product.active')}</SelectItem>
                            <SelectItem value="inactive">{t('product.inactive')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Recherche */}
                      <div>
                        <Label className="text-xs text-muted-foreground mb-1.5 block">{t('vendor.filters.search')}</Label>
                        <div className="relative">
                          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder={t('vendor.filters.searchPlaceholder')}
                            value={productSearchQuery}
                            onChange={(e) => setProductSearchQuery(e.target.value)}
                            className="h-9 pl-9"
                          />
                        </div>
                      </div>
                      
                      {/* Période */}
                      <div>
                        <Label className="text-xs text-muted-foreground mb-1.5 block">{t('vendor.filters.period')}</Label>
                        <Select value={productDateFilter} onValueChange={setProductDateFilter}>
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder={t('vendor.filters.allPeriods')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">{t('vendor.filters.allPeriods')}</SelectItem>
                            <SelectItem value="today">{t('vendor.filters.today')}</SelectItem>
                            <SelectItem value="week">{t('vendor.filters.week')}</SelectItem>
                            <SelectItem value="month">{t('vendor.filters.month')}</SelectItem>
                            <SelectItem value="year">{t('vendor.filters.year')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Trier par */}
                      <div>
                        <Label className="text-xs text-muted-foreground mb-1.5 block">{t('vendor.filters.sortBy')}</Label>
                        <div className="flex gap-1.5">
                          <Select value={productSortBy} onValueChange={setProductSortBy} defaultValue="created_at">
                            <SelectTrigger className="h-9">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="created_at">{t('vendor.filters.createdAt')}</SelectItem>
                              <SelectItem value="title">{t('vendor.filters.name')}</SelectItem>
                              <SelectItem value="price">{t('vendor.filters.price')}</SelectItem>
                              <SelectItem value="stock">{t('vendor.filters.stock')}</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setProductSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                            className="h-9 w-9 p-0"
                          >
                            <ArrowUpDown className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Résultats */}
                    <div className="text-sm text-muted-foreground">
                      {filteredProducts.length} {filteredProducts.length > 1 ? t('vendor.filters.resultsFound') : t('vendor.filters.resultFound')}
                      {filteredProducts.length !== products.length && ` sur ${products.length}`}
                    </div>
                  </div>
                  
                  {products.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>{t('product.noCatalog')}</p>
                      <p className="text-sm">{t('product.addFirst')}</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('product.product')}</TableHead>
                          <TableHead>{t('product.price_label')}</TableHead>
                          <TableHead>{t('product.stock')}</TableHead>
                          <TableHead>{t('product.status')}</TableHead>
                          <TableHead>{t('product.lastUpdate')}</TableHead>
                          <TableHead>{t('product.actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredProducts.map((product) => (
                          <TableRow key={product.id}>
                            <TableCell>
                              <div>
                                <p className="font-medium">
                                  {getTranslatedText(product.title_translations, product.title)}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  {product.category ? formatCategoryLabel(product.category) : ''}
                                </p>
                              </div>
                            </TableCell>
                            <TableCell className="font-mono">
                              {formatPriceWithCurrency(product.price_cents, product.currency, displayCurrency, convertPrice)}
                            </TableCell>
                            <TableCell>
                              <Badge 
                                variant={product.stock <= 5 ? "destructive" : product.stock <= 10 ? "secondary" : "outline"}
                                className={product.stock <= 5 ? "bg-red-100 text-red-800" : product.stock <= 10 ? "bg-orange-100 text-orange-800" : ""}
                              >
                                {product.stock} {t('product.units')}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant={product.active ? "default" : "secondary"}>
                                {product.active ? t('product.active') : t('product.inactive')}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {new Date((product as any).updated_at || product.created_at).toLocaleString('fr-FR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </TableCell>
                            <TableCell>
                              <div className="flex space-x-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditProduct(product)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteProduct(product.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Orders Tab */}
            <TabsContent value="orders" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>{t('orders.recent')}</CardTitle>
                  <CardDescription>
                    {t('orders.track')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Filtres */}
                  <VendorOrderFilters
                    statusFilter={orderStatusFilter}
                    searchQuery={orderSearchQuery}
                    dateFilter={orderDateFilter}
                    sortBy={orderSortBy}
                    sortOrder={orderSortOrder}
                    onStatusChange={setOrderStatusFilter}
                    onSearchChange={setOrderSearchQuery}
                    onDateChange={setOrderDateFilter}
                    onSortByChange={setOrderSortBy}
                    onSortOrderToggle={handleOrderSortOrderToggle}
                    onClearFilters={clearOrderFilters}
                  />

                  {filteredAndSortedOrders.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>{orderSearchQuery || orderStatusFilter !== 'all' || orderDateFilter !== 'all' ? t('orders.noResults') : t('orders.noOrders')}</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('orders.product')}</TableHead>
                          <TableHead>{t('orders.pickupPoint')}</TableHead>
                          <TableHead>{t('orders.amount')}</TableHead>
                          <TableHead>{t('orders.status')}</TableHead>
                          <TableHead>{t('orders.date')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredAndSortedOrders.map((order) => (
                          <TableRow 
                            key={order.id}
                            className="cursor-pointer hover:bg-muted/50 transition-colors"
                            onClick={() => navigate(`/vendor-account/orders/${order.id}`)}
                          >
                            <TableCell>
                              <p className="font-medium">{order.product?.title || 'Produit'}</p>
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{order.relay?.name}</p>
                                <p className="text-sm text-muted-foreground">
                                  {order.relay?.address}
                                </p>
                              </div>
                            </TableCell>
                            <TableCell className="font-mono">
                              {formatPriceWithCurrency(order.amount_cents || 0, order.currency || 'CDF', displayCurrency, convertPrice)}
                            </TableCell>
                            <TableCell>
                              {getStatusBadge(order.status)}
                            </TableCell>
                            <TableCell>
                              {new Date(order.created_at).toLocaleDateString('fr-FR')}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Stock Tab */}
            <TabsContent value="stock" className="space-y-4">
              <Card>
                <CardHeader className="p-4 md:p-6">
                  <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                    <Warehouse className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0" />
                    <span className="truncate">{t('stock.title')}</span>
                  </CardTitle>
                  <CardDescription className="text-xs md:text-sm truncate">
                    {t('stock.description')} {totalStock} {t('stock.units')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Stock faible */}
                    <Card className="border-orange-200">
                      <CardHeader className="p-3 md:p-6">
                        <CardTitle className="text-sm md:text-lg text-orange-800 truncate">
                          {t('stock.lowStock')}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {lowStockProducts === 0 ? (
                          <p className="text-muted-foreground">{t('stock.noLowStock')}</p>
                        ) : (
                          <div className="space-y-2">
                            {products
                              .filter(p => p.stock <= 5)
                              .map(product => (
                                <div key={product.id} className="flex justify-between items-center p-3 bg-orange-50 rounded-lg border border-orange-200">
                                  <div>
                                    <span className="font-medium">{product.title}</span>
                                    <p className="text-xs text-muted-foreground">{product.category ? formatCategoryLabel(product.category) : ''}</p>
                                  </div>
                                  <Badge variant="destructive" className="bg-red-100 text-red-800">
                                    {product.stock} {t('stock.units')}
                                  </Badge>
                                </div>
                              ))
                            }
                          </div>
                        )}
                      </CardContent>

                    </Card>

                    {/* Stock par catégorie */}
                    <Card>
                      <CardHeader className="p-3 md:p-6">
                        <CardTitle className="text-sm md:text-lg truncate">{t('stock.stockByCategory')}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {Array.from(new Set(products.map(p => p.category).filter(Boolean))).map(category => {
                            const categoryProducts = products.filter(p => p.category === category);
                            const totalStock = categoryProducts.reduce((sum, p) => sum + p.stock, 0);
                            return (
                              <div key={category} className="flex justify-between items-center p-2 rounded border">
                                <div>
                                  <span className="capitalize font-medium">{category}</span>
                                  <p className="text-xs text-muted-foreground">{categoryProducts.length} {t('stock.products')}</p>
                                </div>
                                <Badge variant="outline" className="font-mono">
                                  {totalStock} {t('stock.units')}
                                </Badge>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Liste détaillée des stocks */}
                  <Card className="mt-6">
                    <CardHeader>
                      <CardTitle className="text-lg">{t('stock.stockDetails')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t('product.product')}</TableHead>
                            <TableHead>{t('product.category')}</TableHead>
                            <TableHead>{t('stock.currentStock')}</TableHead>
                            <TableHead>{t('stock.state')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {products
                            .sort((a, b) => a.stock - b.stock)
                            .map(product => (
                              <TableRow key={product.id}>
                                <TableCell className="font-medium">{product.title}</TableCell>
                                <TableCell>{product.category ? formatCategoryLabel(product.category) : ''}</TableCell>
                                <TableCell className="font-mono">{product.stock} {t('stock.units')}</TableCell>
                                <TableCell>
                                  <Badge 
                                    variant={product.stock <= 5 ? "destructive" : product.stock <= 10 ? "secondary" : "outline"}
                                    className={
                                      product.stock <= 5 ? "bg-red-100 text-red-800" : 
                                      product.stock <= 10 ? "bg-orange-100 text-orange-800" : 
                                      "bg-green-100 text-green-800"
                                    }
                                  >
                                    {product.stock <= 5 ? t('stock.critical') : product.stock <= 10 ? t('stock.low') : t('stock.good')}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))
                          }
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Managers Tab - Seulement pour les magasins internes et l'administrateur */}
            {(profile?.is_internal || profile?.role === 'admin') && (
              <TabsContent value="managers" className="space-y-4">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Users className="h-5 w-5" />
                          {t('team.title')}
                        </CardTitle>
                        <CardDescription>
                          {t('team.description')}
                        </CardDescription>
                      </div>
                      <Button onClick={() => setShowManagerDialog(true)} className="text-xs md:text-sm h-8 md:h-10 px-2 md:px-4">
                        <Plus className="h-3.5 w-3.5 md:h-4 md:w-4 md:mr-2" />
                        <span className="hidden md:inline ml-2">{t('team.addManager')}</span>
                        <span className="md:hidden ml-1">Ajouter</span>
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {managers.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>{t('team.noManagers')}</p>
                        <p className="text-sm">
                          {t('team.addFromEmployees')}
                        </p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {managers.map((manager) => (
                          <Card key={manager.id}>
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                                    <Users className="h-5 w-5 text-primary" />
                                  </div>
                                  <div>
                                    <p className="font-medium">{manager.manager.full_name}</p>
                                    <p className="text-sm text-muted-foreground">
                                      {manager.manager.phone}
                                    </p>
                                    <Badge variant="outline" className="mt-1">
                                      {manager.manager.system_role}
                                    </Badge>
                                  </div>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveManager(manager.id, manager.manager.full_name)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}

                    {/* Dialog pour ajouter un gestionnaire */}
                    <Dialog open={showManagerDialog} onOpenChange={setShowManagerDialog}>
                      <DialogContent className="max-w-md">
                        <DialogHeader>
                          <DialogTitle>{t('team.addManagerDialog')}</DialogTitle>
                          <DialogDescription>
                            {t('team.selectEmployee')}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="employee">{t('team.employee')}</Label>
                            <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                              <SelectTrigger>
                                <SelectValue placeholder={t('team.chooseEmployee')} />
                              </SelectTrigger>
                              <SelectContent>
                                {employees
                                  .filter(emp => emp.user_id && !managers.some(m => m.manager_id === emp.user_id))
                                  .map((employee) => (
                                    <SelectItem key={employee.id} value={employee.user_id as string}>
                                      <div>
                                        <p className="font-medium">
                                          {employee.first_name} {employee.last_name}
                                        </p>
                                        <p className="text-sm text-muted-foreground">
                                          {employee.position} • {employee.email}
                                        </p>
                                      </div>
                                    </SelectItem>
                                  ))
                                }
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setShowManagerDialog(false)}>
                            {t('team.cancel')}
                          </Button>
                          <Button onClick={handleAddManager}>
                            {t('team.add')}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </CardContent>
                </Card>
              </TabsContent>
            )}

            {/* Settings Tab */}
            <TabsContent value="settings" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Store className="h-5 w-5" />
                    {t('settings.title')}
                  </CardTitle>
                  <CardDescription>
                    {t('settings.description')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleShopSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="business_name">{t('settings.shopName')}</Label>
                        <Input
                          id="business_name"
                          value={shopForm.business_name}
                          onChange={(e) => setShopForm(prev => ({ ...prev, business_name: e.target.value }))}
                          placeholder={t('settings.shopNamePlaceholder')}
                        />
                      </div>
                      <div>
                        <Label htmlFor="business_email">{t('settings.contactEmail')}</Label>
                        <Input
                          id="business_email"
                          type="email"
                          value={shopForm.business_email}
                          onChange={(e) => setShopForm(prev => ({ ...prev, business_email: e.target.value }))}
                          placeholder={t('settings.contactEmailPlaceholder')}
                        />
                      </div>
                      <div>
                        <Label htmlFor="business_phone">{t('settings.businessPhone')}</Label>
                        <Input
                          id="business_phone"
                          type="tel"
                          value={shopForm.business_phone}
                          onChange={(e) => setShopForm(prev => ({ ...prev, business_phone: e.target.value }))}
                          placeholder={t('settings.businessPhonePlaceholder')}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="business_address">{t('settings.mainAddress')}</Label>
                      <Textarea
                        id="business_address"
                        value={shopForm.business_address}
                        onChange={(e) => setShopForm(prev => ({ ...prev, business_address: e.target.value }))}
                        placeholder={t('settings.mainAddressPlaceholder')}
                        rows={3}
                      />
                    </div>

                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <Clock className="h-5 w-5" />
                        <Label className="text-base font-semibold">{t('settings.businessHours')}</Label>
                      </div>
                      <div className="space-y-4">
                        {Object.entries(shopForm.business_hours).map(([day, hours]) => (
                          <div key={day} className="flex items-center gap-4 p-4 border rounded-lg">
                            <div className="w-20 font-medium capitalize">
                              {day === 'monday' && t('settings.monday')}
                              {day === 'tuesday' && t('settings.tuesday')}
                              {day === 'wednesday' && t('settings.wednesday')}
                              {day === 'thursday' && t('settings.thursday')}
                              {day === 'friday' && t('settings.friday')}
                              {day === 'saturday' && t('settings.saturday')}
                              {day === 'sunday' && t('settings.sunday')}
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={!hours.closed}
                                onCheckedChange={(checked) => updateBusinessHours(day, 'closed', !checked)}
                              />
                              <span className="text-sm">{t('settings.open')}</span>
                            </div>
                            
                            {!hours.closed && (
                              <>
                                <div className="flex items-center gap-2">
                                  <Label className="text-sm">{t('settings.opening')}</Label>
                                  <Input
                                    type="time"
                                    value={hours.open}
                                    onChange={(e) => updateBusinessHours(day, 'open', e.target.value)}
                                    className="w-24"
                                  />
                                </div>
                                <div className="flex items-center gap-2">
                                  <Label className="text-sm">{t('settings.closing')}</Label>
                                  <Input
                                    type="time"
                                    value={hours.close}
                                    onChange={(e) => updateBusinessHours(day, 'close', e.target.value)}
                                    className="w-24"
                                  />
                                </div>
                              </>
                            )}
                            
                            {hours.closed && (
                              <span className="text-sm text-muted-foreground">{t('settings.closed')}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-4 border rounded-lg p-4">
                      <div>
                        <Label className="text-base font-semibold">{t('settings.withdrawalDefaults')}</Label>
                        <p className="text-sm text-muted-foreground">{t('settings.withdrawalDefaultsDesc')}</p>
                      </div>

                      <div>
                        <Label htmlFor="default_withdrawal_method">Moyen de versement par défaut</Label>
                        <Select
                          value={shopForm.default_withdrawal_method || 'bank_transfer'}
                          onValueChange={(value) => setShopForm(prev => ({ ...prev, default_withdrawal_method: value }))}
                        >
                          <SelectTrigger id="default_withdrawal_method">
                            <SelectValue placeholder="Choisir le moyen par défaut" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="bank_transfer">Compte bancaire</SelectItem>
                            <SelectItem value="mobile_money">Numéro mobile</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">
                          Le système utilisera ce moyen pour les retraits automatiques si les informations sont présentes.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="default_bank_first_name">Prenom du titulaire</Label>
                          <Input
                            id="default_bank_first_name"
                            value={shopForm.default_bank_first_name}
                            onChange={(e) => setShopForm(prev => ({ ...prev, default_bank_first_name: e.target.value }))}
                            placeholder="Ex: Jean"
                          />
                        </div>
                        <div>
                          <Label htmlFor="default_bank_last_name">Nom du titulaire</Label>
                          <Input
                            id="default_bank_last_name"
                            value={shopForm.default_bank_last_name}
                            onChange={(e) => setShopForm(prev => ({ ...prev, default_bank_last_name: e.target.value }))}
                            placeholder="Ex: Dupont"
                          />
                        </div>
                        <div>
                          <Label htmlFor="default_bank_name">{t('settings.bankName')}</Label>
                          <Input
                            id="default_bank_name"
                            value={shopForm.default_bank_name}
                            onChange={(e) => setShopForm(prev => ({ ...prev, default_bank_name: e.target.value }))}
                            placeholder={t('settings.bankNamePlaceholder')}
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="default_bank_account">{t('settings.bankAccount')}</Label>
                        <Input
                          id="default_bank_account"
                          value={shopForm.default_bank_account}
                          onChange={(e) => setShopForm(prev => ({ ...prev, default_bank_account: e.target.value }))}
                          placeholder={t('settings.bankAccountPlaceholder')}
                        />
                      </div>

                      {/* Mobile Money */}
                      <div className="space-y-3 border-t pt-4">
                        <Label className="text-sm font-semibold">Mobile Money</Label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="default_mobile_provider">Opérateur</Label>
                            <Select
                              value={shopForm.default_mobile_provider || ''}
                              onValueChange={(value) => setShopForm(prev => ({ ...prev, default_mobile_provider: value }))}
                            >
                              <SelectTrigger id="default_mobile_provider">
                                <SelectValue placeholder="Choisir l'opérateur" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="MPESA">M-Pesa (Vodacom)</SelectItem>
                                <SelectItem value="AIRTEL">Airtel Money</SelectItem>
                                <SelectItem value="ORANGE">Orange Money</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor="default_mobile_money_number">Numéro Mobile Money</Label>
                            <Input
                              id="default_mobile_money_number"
                              value={shopForm.default_mobile_money_number || ''}
                              onChange={(e) => setShopForm(prev => ({ ...prev, default_mobile_money_number: e.target.value }))}
                              placeholder="Ex: +243812345678"
                            />
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Ce numéro sera pré-rempli automatiquement lors d'une demande de retrait. Toute modification est soumise à validation admin.
                        </p>
                      </div>

                      <div className="space-y-4 border-t pt-4">
                        <div>
                          <Label className="text-base font-semibold">Retraits automatiques</Label>
                          <p className="text-sm text-muted-foreground">
                            Planifiez des versements automatiques selon une cadence quotidienne, hebdomadaire ou mensuelle.
                          </p>
                        </div>

                        <div className="flex items-center gap-3">
                          <Switch
                            checked={shopForm.auto_withdrawal_enabled}
                            onCheckedChange={(checked) => setShopForm(prev => ({ ...prev, auto_withdrawal_enabled: checked }))}
                          />
                          <Label>Activer les retraits automatiques</Label>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="auto_withdrawal_frequency">Fréquence</Label>
                            <Select
                              value={shopForm.auto_withdrawal_frequency}
                              onValueChange={(value) => setShopForm(prev => ({ ...prev, auto_withdrawal_frequency: value }))}
                            >
                              <SelectTrigger id="auto_withdrawal_frequency">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="daily">Tous les jours</SelectItem>
                                <SelectItem value="weekly">Toutes les semaines</SelectItem>
                                <SelectItem value="monthly">Tous les mois</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <Label htmlFor="auto_withdrawal_time">Heure d'exécution</Label>
                            <Input
                              id="auto_withdrawal_time"
                              type="time"
                              value={shopForm.auto_withdrawal_time}
                              onChange={(e) => setShopForm(prev => ({ ...prev, auto_withdrawal_time: e.target.value }))}
                            />
                          </div>

                          {shopForm.auto_withdrawal_frequency === 'weekly' && (
                            <div>
                              <Label htmlFor="auto_withdrawal_day_of_week">Jour de la semaine</Label>
                              <Select
                                value={shopForm.auto_withdrawal_day_of_week}
                                onValueChange={(value) => setShopForm(prev => ({ ...prev, auto_withdrawal_day_of_week: value }))}
                              >
                                <SelectTrigger id="auto_withdrawal_day_of_week">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="0">Dimanche</SelectItem>
                                  <SelectItem value="1">Lundi</SelectItem>
                                  <SelectItem value="2">Mardi</SelectItem>
                                  <SelectItem value="3">Mercredi</SelectItem>
                                  <SelectItem value="4">Jeudi</SelectItem>
                                  <SelectItem value="5">Vendredi</SelectItem>
                                  <SelectItem value="6">Samedi</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          {shopForm.auto_withdrawal_frequency === 'monthly' && (
                            <>
                              <div>
                                <Label htmlFor="auto_withdrawal_day_of_month">Jour du mois</Label>
                                <Input
                                  id="auto_withdrawal_day_of_month"
                                  type="number"
                                  min="1"
                                  max="31"
                                  value={shopForm.auto_withdrawal_day_of_month}
                                  onChange={(e) => setShopForm(prev => ({ ...prev, auto_withdrawal_day_of_month: e.target.value }))}
                                />
                              </div>
                              <div>
                                <Label htmlFor="auto_withdrawal_window_start_day">Début de fenêtre</Label>
                                <Input
                                  id="auto_withdrawal_window_start_day"
                                  type="number"
                                  min="1"
                                  max="31"
                                  value={shopForm.auto_withdrawal_window_start_day}
                                  onChange={(e) => setShopForm(prev => ({ ...prev, auto_withdrawal_window_start_day: e.target.value }))}
                                />
                              </div>
                              <div>
                                <Label htmlFor="auto_withdrawal_window_end_day">Fin de fenêtre</Label>
                                <Input
                                  id="auto_withdrawal_window_end_day"
                                  type="number"
                                  min="1"
                                  max="31"
                                  value={shopForm.auto_withdrawal_window_end_day}
                                  onChange={(e) => setShopForm(prev => ({ ...prev, auto_withdrawal_window_end_day: e.target.value }))}
                                />
                              </div>
                            </>
                          )}

                          <div>
                            <Label htmlFor="auto_withdrawal_min_balance_cents">Seuil minimum à conserver</Label>
                            <Input
                              id="auto_withdrawal_min_balance_cents"
                              type="number"
                              min="0"
                              step="1"
                              value={shopForm.auto_withdrawal_min_balance_cents}
                              onChange={(e) => setShopForm(prev => ({ ...prev, auto_withdrawal_min_balance_cents: e.target.value }))}
                              placeholder="0"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Montant minimum à laisser sur le compte avant d'exécuter le retrait automatique.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="border rounded-lg p-4 bg-muted/30 space-y-3">
                      <div>
                        <Label className="text-base font-semibold">Validation des changements sensibles</Label>
                        <p className="text-sm text-muted-foreground">
                          Les changements de numero mobile magasin et de compte bancaire sont appliques uniquement apres validation.
                        </p>
                      </div>

                      {paymentChangeRequests.length === 0 ? (
                        <p className="text-sm text-muted-foreground">Aucune demande recente.</p>
                      ) : (
                        <div className="space-y-3">
                          {paymentChangeRequests.map((request) => {
                            const isPending = request.status === 'pending';
                            const roleText = String(
                              (profile as any)?.system_role ||
                              (profile as any)?.user_role ||
                              (profile as any)?.old_role ||
                              (profile as any)?.role ||
                              ''
                            ).toLowerCase();
                            const isAdminActor = roleText === 'admin' || roleText === 'super_admin';
                            const isPrimaryVendor = user?.id === request.vendor_id;
                            const canSelfValidate = isPrimaryVendor || isAdminActor;
                            const canReview = isPending && (request.requested_by !== user?.id || canSelfValidate);

                            return (
                              <div key={request.id} className="border rounded-md p-3 space-y-2">
                                <div className="flex items-center justify-between gap-3">
                                  <p className="text-sm font-medium">Demande du {new Date(request.created_at).toLocaleString('fr-FR')}</p>
                                  <Badge variant={request.status === 'approved' ? 'default' : request.status === 'rejected' ? 'destructive' : 'secondary'}>
                                    {request.status === 'approved' ? 'Approuvee' : request.status === 'rejected' ? 'Rejetee' : 'En attente'}
                                  </Badge>
                                </div>
                                <div className="text-xs text-muted-foreground space-y-1">
                                  <p>Mobile magasin: {request.proposed_business_phone || '-'}</p>
                                  <p>Compte bancaire: {request.proposed_bank_account || '-'}</p>
                                  <p>Banque: {request.proposed_bank_name || '-'}</p>
                                  <p>Titulaire: {[request.proposed_bank_first_name, request.proposed_bank_last_name].filter(Boolean).join(' ') || profile?.business_name || shopForm.business_name || '-'}</p>
                                  {request.rejection_reason && <p>Raison rejet: {request.rejection_reason}</p>}
                                </div>

                                {canReview && (
                                  <div className="flex gap-2 pt-1">
                                    <Button
                                      type="button"
                                      size="sm"
                                      onClick={() => handleReviewPaymentChangeRequest(request.id, true)}
                                    >
                                      Valider
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="destructive"
                                      size="sm"
                                      onClick={() => handleReviewPaymentChangeRequest(request.id, false)}
                                    >
                                      Rejeter
                                    </Button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end">
                      <Button type="submit">
                        {t('settings.saveInfo')}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Finances Tab */}
            <TabsContent value="finances" className="space-y-6">
              <Tabs defaultValue="balance" className="w-full">
                <TabsList>
                  <TabsTrigger value="balance">Solde et Transactions</TabsTrigger>
                  <TabsTrigger value="withdrawals">Retraits</TabsTrigger>
                  <TabsTrigger value="invoices">Factures</TabsTrigger>
                  <TabsTrigger value="analytics">Statistiques</TabsTrigger>
                </TabsList>
                
                <TabsContent value="balance">
                  <VendorBalance />
                </TabsContent>
                
                <TabsContent value="withdrawals">
                  <VendorWithdrawalRequest />
                </TabsContent>
                
                <TabsContent value="invoices">
                  <VendorInvoiceGenerator />
                </TabsContent>
                
                <TabsContent value="analytics">
                  <VendorFinancialDashboard />
                </TabsContent>
              </Tabs>
            </TabsContent>

            {/* Notifications Tab */}
            <TabsContent value="notifications" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Paramètres de notification</CardTitle>
                  <CardDescription>
                    Gérez comment et quand vous souhaitez recevoir des notifications
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <NotificationSettings />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default VendorAccount;